import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { toast } from 'sonner';
import { authTokens } from '@/services/authTokens';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5100';

export const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30_000,
});

// Request interceptor: anexa JWT
api.interceptors.request.use((config) => {
  const token = authTokens.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Refresh handling — serializa requests concorrentes
let refreshPromise: Promise<string | null> | null = null;
async function tryRefresh(): Promise<string | null> {
  const rt = authTokens.getRefreshToken();
  if (!rt) return null;
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const resp = await axios.post(`${API_URL}/api/auth/refresh`, { refresh_token: rt });
      const data = resp.data ?? {};
      authTokens.setTokens({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt: data.expires_at,
        refreshExpiresAt: data.refresh_expires_at,
      });
      return data.access_token as string;
    } catch {
      authTokens.clear();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

// Response interceptor: trata erros comuns
api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError<any>) => {
    if (!error.response) {
      toast.error('Erro de conexão. Verifique sua internet.');
      return Promise.reject(error);
    }
    const { status, data } = error.response;
    const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
    switch (status) {
      case 401:
        // Endpoints públicos não disparam refresh (evita loops em /login etc.)
        if (original && !original._retry && !original.url?.includes('/api/auth/')) {
          const newToken = await tryRefresh();
          if (newToken) {
            original._retry = true;
            original.headers = { ...(original.headers ?? {}), Authorization: `Bearer ${newToken}` };
            return api.request(original);
          }
          // Token de impersonação expira sem refresh — em vez de deslogar,
          // volta pra sessão do operador (master/parceiro) e recarrega.
          if (authTokens.hasImpersonationBackup()) {
            authTokens.endImpersonation();
            toast.error('Sessão de impersonação expirada. Voltando à sua conta.');
            window.location.href = '/';
            break;
          }
          authTokens.clear();
          if (!window.location.pathname.startsWith('/login')) {
            window.location.href = '/login';
          }
        }
        break;
      case 402:
        toast.error(data?.message || 'Funcionalidade requer upgrade do plano.', { duration: 6000 });
        break;
      case 403:
        toast.error(data?.message || 'Sem permissão para esta ação.');
        break;
      case 423:
        toast.error(
          data?.message ||
            (data?.access === 'read_only'
              ? 'Seu período de avaliação acabou. Assine para continuar editando.'
              : 'Sua assinatura está inativa. Reative para voltar a editar.'),
          { duration: 8000 },
        );
        break;
      case 429:
        toast.error('Muitas requisições. Aguarde alguns segundos.');
        break;
      case 500:
        toast.error('Erro no servidor. Tente novamente em instantes.');
        break;
    }
    return Promise.reject(error);
  },
);

// Endpoints helpers tipados
export const apiClient = {
  // Auth (interna — sem Supabase)
  login: (email: string, password: string) =>
    api.post('/api/auth/login', { email, password }),
  refresh: (refreshToken: string) =>
    api.post('/api/auth/refresh', { refresh_token: refreshToken }),
  logout: (refreshToken?: string | null) =>
    api.post('/api/auth/logout', refreshToken ? { refresh_token: refreshToken } : {}),
  forgotPassword: (email: string) =>
    api.post('/api/auth/forgot-password', { email }),
  resetPassword: (token: string, newPassword: string) =>
    api.post('/api/auth/reset-password', { token, new_password: newPassword }),
  acceptInvite: (token: string, password: string) =>
    api.post('/api/auth/accept-invite', { token, password }),
  acceptPartnerInvite: (token: string, password: string) =>
    api.post('/api/auth/partner/accept-invite', { token, password }),
  signup: (data: any) => api.post('/api/auth/signup', data),
  checkSubdomain: (subdomain: string) => api.get('/api/auth/check-subdomain', { params: { subdomain } }),

  // Plans
  getPlans: () => api.get('/api/plans'),

  // Tenant
  getTenant: () => api.get('/api/tenant'),
  updateTenant: (data: any) => api.put('/api/tenant', data),
  getTenantUsers: () => api.get('/api/tenant/users'),
  inviteTenantUser: (data: { email: string; fullName: string; role: 'admin' | 'editor' | 'viewer'; password?: string; phone?: string }) =>
    api.post('/api/tenant/users', {
      email: data.email,
      full_name: data.fullName,
      role: data.role,
      password: data.password,
      phone: data.phone,
    }),
  updateTenantUser: (id: number, data: { fullName?: string; phone?: string; role?: 'admin' | 'editor' | 'viewer'; isActive?: boolean }) =>
    api.patch(`/api/tenant/users/${id}`, {
      full_name: data.fullName,
      phone: data.phone,
      role: data.role,
      is_active: data.isActive,
    }),
  deleteTenantUser: (id: number) => api.delete(`/api/tenant/users/${id}`),
  resendInvite: (id: number) => api.post(`/api/tenant/users/${id}/resend-invite`),

  // Stores (multi-loja)
  getStores: () => api.get('/api/signage/stores'),
  getStore: (id: number) => api.get(`/api/signage/stores/${id}`),
  createStore: (data: {
    code: string;
    name: string;
    short_name?: string | null;
    cnpj?: string | null;
    phone?: string | null;
    timezone?: string | null;
    is_active?: boolean;
    sort_order?: number;
    notes?: string | null;
  }) => api.post('/api/signage/stores', data),
  updateStore: (id: number, data: Record<string, any>) =>
    api.put(`/api/signage/stores/${id}`, data),
  setDefaultStore: (id: number) =>
    api.post(`/api/signage/stores/${id}/set-default`),
  deleteStore: (id: number, reassignTo?: number) =>
    api.delete(`/api/signage/stores/${id}`, { params: reassignTo ? { reassign_to: reassignTo } : undefined }),

  // TVs
  getTvs: (params?: { search?: string; status?: string; store_id?: number }) =>
    api.get('/api/signage/tvs', { params }),
  getTvsStats: () => api.get('/api/signage/tvs/stats'),
  getTvSessions: (params?: { tv_id?: number; store_id?: number; from?: string; to?: string }) =>
    api.get('/api/signage/tvs/sessions', { params }),
  getTv: (id: number) => api.get(`/api/signage/tvs/${id}`),
  updateTv: (id: number, data: any) => api.put(`/api/signage/tvs/${id}`, data),
  deleteTv: (id: number) => api.delete(`/api/signage/tvs/${id}`),
  assignPlaylistToTv: (id: number, playlistId: number) =>
    api.post(`/api/signage/tvs/${id}/playlist`, { playlist_id: playlistId }),

  // Devices (pareamento)
  pairDevice: (pairingCode: string, name?: string, location?: string) =>
    api.post('/api/signage/devices/pair', { pairing_code: pairingCode, name, location }),

  // Playlist Templates
  getPlaylistTemplates: (segment?: string) =>
    api.get('/api/signage/playlist-templates', { params: { segment } }),
  getPlaylistTemplate: (code: string) =>
    api.get(`/api/signage/playlist-templates/${code}`),
  usePlaylistTemplate: (code: string, name?: string) =>
    api.post(`/api/signage/playlist-templates/${code}/use`, { name }),

  // Playlists
  getPlaylists: () => api.get('/api/signage/playlists'),
  getPlaylist: (id: number) => api.get(`/api/signage/playlists/${id}`),
  createPlaylist: (data: any) => api.post('/api/signage/playlists', data),
  updatePlaylist: (id: number, data: any) => api.put(`/api/signage/playlists/${id}`, data),
  deletePlaylist: (id: number) => api.delete(`/api/signage/playlists/${id}`),
  duplicatePlaylist: (id: number) => api.post(`/api/signage/playlists/${id}/duplicate`),
  assignPlaylistToTvs: (id: number, tvIds: number[], makeDefault = false) =>
    api.post(`/api/signage/playlists/${id}/assign-tvs`, { tv_ids: tvIds, make_default: makeDefault }),

  // Slides
  createSlide: (data: any) => api.post('/api/signage/slides', data),
  updateSlide: (id: number, data: any) => api.put(`/api/signage/slides/${id}`, data),
  deleteSlide: (id: number) => api.delete(`/api/signage/slides/${id}`),
  reorderSlides: (playlistId: number, orderedIds: number[]) =>
    api.put('/api/signage/slides/reorder', { playlist_id: playlistId, ordered_slide_ids: orderedIds }),
  getUploadUrl: (type: 'image' | 'video' | 'product' | 'logo', filename: string) =>
    api.post('/api/signage/slides/upload-url', { type, filename }),

  // Themes
  getThemes: (segment?: string) => api.get('/api/signage/themes', { params: { segment } }),

  // Products
  // paramsSerializer { indexes: null } → arrays viram chaves repetidas (categoria=A&categoria=B),
  // que o ASP.NET liga em string[]. Valores únicos serializam normalmente.
  getProducts: (params?: any) => api.get('/api/signage/products', { params, paramsSerializer: { indexes: null } }),
  getProductCategories: () => api.get('/api/signage/products/categories'),
  getProductDepartments: () => api.get('/api/signage/products/departments'),
  getProduct: (codigo: string) => api.get(`/api/signage/products/${encodeURIComponent(codigo)}`),
  createProduct: (data: any) => api.post('/api/signage/products', data),
  updateProduct: (codigo: string, data: any) => api.put(`/api/signage/products/${encodeURIComponent(codigo)}`, data),
  deleteProduct: (codigo: string) => api.delete(`/api/signage/products/${encodeURIComponent(codigo)}`),
  uploadProducts: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/api/signage/products/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  wipeProducts: (confirmation: string) =>
    api.post('/api/signage/products/wipe', { confirmation }),
  searchProductImages: (q: string, limit = 10) =>
    api.get('/api/signage/products/image-search', { params: { q, limit } }),

  // Product categories (managed list)
  getProductCategoriesManaged: () => api.get('/api/signage/product-categories'),
  createProductCategory: (nome: string, sortOrder = 0) =>
    api.post('/api/signage/product-categories', { nome, sortOrder }),
  renameProductCategory: (oldNome: string, nome: string, sortOrder?: number) =>
    api.put(`/api/signage/product-categories/${encodeURIComponent(oldNome)}`, { nome, sortOrder }),
  deleteProductCategory: (nome: string, force = false) =>
    api.delete(`/api/signage/product-categories/${encodeURIComponent(nome)}`, { params: { force } }),

  // Product departments (managed list)
  getProductDepartmentsManaged: () => api.get('/api/signage/product-departments'),
  createProductDepartment: (nome: string, sortOrder = 0) =>
    api.post('/api/signage/product-departments', { nome, sortOrder }),
  renameProductDepartment: (oldNome: string, nome: string, sortOrder?: number) =>
    api.put(`/api/signage/product-departments/${encodeURIComponent(oldNome)}`, { nome, sortOrder }),
  deleteProductDepartment: (nome: string, force = false) =>
    api.delete(`/api/signage/product-departments/${encodeURIComponent(nome)}`, { params: { force } }),

  // Promotions
  getPromotions: () => api.get('/api/signage/promotions'),

  // Agent (legado — heartbeat 1:1 do agent Go atual)
  getAgentStatus: () => api.get('/api/signage/agent/status'),

  // Agent ETL (addon — migration 022) — admin
  agentEtl: {
    listAgents: () => api.get('/api/signage/agent-admin/agents'),
    generateInstallToken: (label?: string) =>
      api.post('/api/signage/agent-admin/agents/install-token', { label }),
    getInstallToken: (agentId: string) =>
      api.get(`/api/signage/agent-admin/agents/${agentId}/install-token`),
    revokeAgent: (id: string) => api.post(`/api/signage/agent-admin/agents/${id}/revoke`),
    deleteAgent: (id: string) => api.delete(`/api/signage/agent-admin/agents/${id}`),

    listConnections: () => api.get('/api/signage/agent-admin/connections'),
    createConnection: (data: { agent_id: string; name: string; db_type: string; server_hint?: string; database_hint?: string }) =>
      api.post('/api/signage/agent-admin/connections', data),
    deleteConnection: (id: number) => api.delete(`/api/signage/agent-admin/connections/${id}`),

    listRoutines: (connectionId?: number) =>
      api.get('/api/signage/agent-admin/routines', { params: { connectionId } }),
    createRoutine: (data: any) => api.post('/api/signage/agent-admin/routines', data),
    updateRoutine: (id: number, data: any) => api.put(`/api/signage/agent-admin/routines/${id}`, data),
    deleteRoutine: (id: number) => api.delete(`/api/signage/agent-admin/routines/${id}`),
    triggerRoutine: (id: number) => api.post(`/api/signage/agent-admin/routines/${id}/trigger`),
    seedVisualErpRoutines: (connectionId: number, onlyDiscovery = false) =>
      api.post('/api/signage/agent-admin/routines/seed-visual-erp', {
        connection_id: connectionId,
        only_discovery: onlyDiscovery,
      }),

    // ERP Categories (migration 023)
    listErpCategories: () => api.get('/api/signage/agent-admin/erp-categories'),
    saveCategorySelection: (data: { codigos_externos?: string[]; nomes?: string[] }) =>
      api.put('/api/signage/agent-admin/erp-categories/selection', data),

    listExecutions: (params?: { routineId?: number; agentId?: string; status?: string; since?: string; limit?: number }) =>
      api.get('/api/signage/agent-admin/executions', { params }),

    // Validador SQL inline (anônimo — usado no Monaco editor)
    validateSql: (sqlQuery: string) =>
      api.post('/api/signage/agent-etl/admin/validate-sql', { sql_query: sqlQuery }),

    // Downloads (Etapa 7)
    listDownloads: () => api.get('/api/signage/agent-admin/downloads'),
    /** Resolve a URL final do binário (CDN público ou path interno autenticado). */
    getDownloadUrl: (platform: string) =>
      api.get(`/api/signage/agent-admin/downloads/${platform}/url`),
    /** Fallback: baixa o binário via blob (usado quando source=local). */
    downloadBinary: (platform: string) =>
      api.get(`/api/signage/agent-admin/downloads/${platform}`, {
        responseType: 'blob',
      }),
  },

  // Billing
  getSubscription: () => api.get('/api/billing/subscription'),
  getInvoices: () => api.get('/api/billing/invoices'),
  getInvoice: (id: number) => api.get(`/api/billing/invoices/${id}`),
  activateSubscription: (data: any) => api.post('/api/billing/activate', data),
  /** Atualiza tv_count / plan_id / billing_cycle sem exigir PaymentMethod.
   *  Funciona em trial e em assinaturas ativas. */
  updateSubscription: (data: { plan_id?: number; tv_count?: number; billing_cycle?: 'monthly' | 'annual' }) =>
    api.patch('/api/billing/subscription', data),
  /** Gera nova cobrança no gateway pra uma invoice pendente/atrasada
   *  (útil quando a URL expirou ou cliente quer mudar a forma de pagamento). */
  regenerateInvoice: (id: number, paymentMethod?: 'pix' | 'boleto' | 'credit_card') =>
    api.post(`/api/billing/invoices/${id}/regenerate`, { payment_method: paymentMethod }),
  /** Consulta o Asaas e atualiza o status da invoice localmente.
   *  Útil quando o webhook falhou e a baixa não veio automática. */
  syncInvoiceStatus: (id: number) =>
    api.post(`/api/billing/invoices/${id}/sync-status`),
  /** Tokeniza cartão de crédito junto ao Asaas. NUNCA log/salva esses dados localmente. */
  addCreditCard: (data: {
    holder_name: string; number: string; expiry_month: string; expiry_year: string;
    ccv: string; postal_code: string; address_number: string;
  }) => api.post('/api/billing/payment-methods/credit-card', data),
  cancelSubscription: (reason: string) => api.post('/api/billing/cancel', { reason }),

  // Master admin
  admin: {
    listTenants: (params?: any) => api.get('/api/admin/tenants', { params }),
    getTenant: (id: number) => api.get(`/api/admin/tenants/${id}`),
    getTenantStats: (id: number) => api.get(`/api/admin/tenants/${id}/stats`),
    updateTenantStatus: (id: number, status: string, reason?: string) =>
      api.put(`/api/admin/tenants/${id}/status`, { status, reason }),
    extendTrial: (id: number, days: number) =>
      api.post(`/api/admin/tenants/${id}/extend-trial`, { days }),
    activateManual: (id: number, planId?: number, reason?: string) =>
      api.post(`/api/admin/tenants/${id}/activate`, { plan_id: planId, reason }),
    listPlans: () => api.get('/api/admin/plans'),
    changePlan: (id: number, planId: number) =>
      api.put(`/api/admin/tenants/${id}/plan`, { plan_id: planId }),
    /** Vincula tenant a um parceiro (partnerId) ou deixa na conta principal (null). */
    setTenantPartner: (id: number, partnerId: number | null) =>
      api.put(`/api/admin/tenants/${id}/partner`, { partner_id: partnerId }),
    /** Master entra como o tenant. Retorna access_token de impersonação. */
    impersonateTenant: (id: number) =>
      api.post(`/api/admin/tenants/${id}/impersonate`),
    notificationPreview: (id: number) =>
      api.get(`/api/admin/tenants/${id}/notification-preview`),
    notificationTest: (id: number, body: { template_code: string; channel: string; recipient: string; days_left?: number }) =>
      api.post(`/api/admin/tenants/${id}/notification-test`, body),
    getMetrics: () => api.get('/api/admin/metrics'),
    getOverdueInvoices: () => api.get('/api/admin/invoices/overdue'),

    // Parceiros / revendas (master)
    listPartners: (params?: { search?: string; limit?: number; offset?: number }) =>
      api.get('/api/admin/partners', { params }),
    getPartner: (id: number) => api.get(`/api/admin/partners/${id}`),
    createPartner: (data: { name: string; email: string; phone?: string; notes?: string }) =>
      api.post('/api/admin/partners', data),
    updatePartner: (id: number, data: { name?: string; phone?: string; notes?: string; status?: string }) =>
      api.put(`/api/admin/partners/${id}`, data),
    resendPartnerInvite: (id: number) => api.post(`/api/admin/partners/${id}/resend-invite`),
  },

  // Área do parceiro/revenda
  partner: {
    me: () => api.get('/api/partner/me'),
    listClients: (params?: { search?: string; limit?: number; offset?: number }) =>
      api.get('/api/partner/clients', { params }),
    getClient: (id: number) => api.get(`/api/partner/clients/${id}`),
    createClient: (data: {
      razaoSocial: string; nomeFantasia?: string; cnpj?: string; cpf?: string;
      subdomain?: string; emailPrimary: string; contactName: string; phone?: string; segment?: string;
      addressCidade?: string; addressEstado?: string;
    }) => api.post('/api/partner/clients', {
      razao_social: data.razaoSocial,
      nome_fantasia: data.nomeFantasia,
      cnpj: data.cnpj,
      cpf: data.cpf,
      subdomain: data.subdomain,
      email_primary: data.emailPrimary,
      contact_name: data.contactName,
      phone: data.phone,
      segment: data.segment,
      address_cidade: data.addressCidade,
      address_estado: data.addressEstado,
    }),
    resendClientInvite: (id: number) => api.post(`/api/partner/clients/${id}/resend-invite`),
    /** Parceiro entra como o cliente. Retorna access_token de impersonação. */
    impersonateClient: (id: number) => api.post(`/api/partner/clients/${id}/impersonate`),
  },
};
